import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "uh-cookie-consent-v1";

/**
 * Cookie / Tracking consent banner.
 * - Persists choice in localStorage.
 * - Two outcomes: "accept" or "decline" — exposed via window event "uh-consent"
 *   so any future analytics loader can listen and conditionally initialize.
 * - Stays out of the way (bottom-fixed, dismissible).
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch {
      // private mode / disabled storage — fail open (show banner each time)
      setVisible(true);
    }
  }, []);

  const persist = (choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(new CustomEvent("uh-consent", { detail: { choice } }));
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[70] px-4 sm:px-6 md:px-10 pb-4 sm:pb-6"
    >
      <div
        role="dialog"
        aria-label="Cookie consent"
        className="mx-auto max-w-[1100px] border border-bone/15 bg-obsidian/95 backdrop-blur-xl text-bone shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
      >
        <div className="p-5 sm:p-6 md:p-7 grid md:grid-cols-[1fr_auto] gap-5 md:gap-8 items-center">
          <div>
            <p className="label-eyebrow text-brass">Cookies &amp; Tracking</p>
            <p className="mt-2 text-sm md:text-[15px] leading-relaxed text-bone/80">
              We use a small number of cookies to operate the site, remember your preferences, and measure performance.
              You can accept or decline non-essential tracking. Read our{" "}
              <Link to="/privacy" data-testid="cookie-banner-privacy-link" className="text-brass hover:text-champagne underline-offset-4 hover:underline">
                Privacy Policy
              </Link>
              {" "}for details.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:shrink-0">
            <button
              type="button"
              data-testid="cookie-decline"
              onClick={() => persist("decline")}
              className="px-5 py-3 border border-bone/30 text-bone text-[11px] uppercase tracking-[0.3em] hover:border-bone/60 transition-colors"
            >
              Decline
            </button>
            <button
              type="button"
              data-testid="cookie-accept"
              onClick={() => persist("accept")}
              className="px-5 py-3 bg-brass text-obsidian text-[11px] uppercase tracking-[0.3em] hover:bg-champagne transition-colors"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
